import { Button } from '@/components/ui/button';
import { Edit, Eye, Download, History, PauseCircle, RotateCcw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { cleanStandardName } from '@/components/ui/FrameworkCombobox';
import React, { useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Task } from '../mockTasks';
import { renderDate } from '../../../../../shared/renderDate';
import { useTenancy } from '@/contexts/TenancyContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// @ts-ignore
import { faClipboardListCheck, faPencil } from '@awesome.me/kit-269fbef2e2/icons/duotone/solid';
import { getHelpContentByPage } from '@/services/HelpPopoverService';
import HelpPopoverHover from '@/components/HelpPopoverHover';
import { TOUR_STEP_IDS } from '@/lib/tour-constants';

// Define valid badge variants
type BadgeVariant = 'error' | 'code' | 'form' | 'template' | 'image' | 'default' | 'destructive' | 'outline' | 'secondary' | 'presentation' | 'manual' | 'active' | 'pdf' | 'database' | 'medium' | 'other';

// Utility to get document type variant for Badge component
const getDocTypeVariant = (type: string): BadgeVariant => {
  if (typeof type !== 'string') return 'other';
  const lowerType = type.toLowerCase();
  switch (lowerType) {
    case 'policy':
    case 'procedure':
    case 'form':
    case 'report':
    case 'record':
    case 'manual':
    case 'plan':
    case 'guideline':
    case 'standard':
    case 'checklist':
    case 'matrix':
    case 'presentation':
    case 'pdf':
    case 'evidence':
    case 'image':
    case 'spreadsheet':
    case 'database':
    case 'code':
    case 'website':
    case 'template':
    case 'hardware':
    case 'software':
    case 'service':
    case 'personnel':
    case 'documentation':
      return lowerType as BadgeVariant;
    default:
      return 'other' as const;
  }
};

// Utility to get status variant for Badge component
const getStatusVariant = (status: string): string => {
  const lowerStatus = status.toLowerCase();
  switch (lowerStatus) {
    case 'current':
      return 'active';
    case 'draft':
      return 'inactive';
    case 'expired':
      return 'decommissioned';
    default:
      return 'secondary';
  }
};

export const getTaskColumns = (helpContent: any, onActions: {
  onEdit?: (row: Task) => void;
  onPerform?: (row: Task) => void;
  onDownload?: (row: Task) => void;
  onHistory?: (row: Task) => void;
  onRestore?: (row: Task) => void;
}, isReadOnly: boolean = false, showDeletedTasks: boolean = false): ColumnDef<Task, any>[] => {
  // Use our custom hook to get the user name function
  console.log('Found help content: ', helpContent);
  return [
    {
      header: () => <span className="bg-transparent px-2 py-1 rounded font-semibold text-center block whitespace-normal break-words">
        <HelpPopoverHover config={helpContent?.['table.header.assurancetask']}>Assurance Task</HelpPopoverHover>
      </span>,
      accessorKey: 'checkDetail.check',
      cell: ({ row, getValue }) => {
        const task = row.original;
        const isActive = task.checkDetail?.active === 'true' || task.checkDetail?.active === true;
        return (
          <div className="flex items-center gap-2">
            {!isActive && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center justify-center flex-shrink-0">
                      <PauseCircle className="h-4 w-4 text-amber-500" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs">
                    <p className="font-medium">Inactive Task</p>
                    <p className="text-xs text-muted-foreground">This task is currently paused and won't appear in active task lists or reminders.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <span className={`text-left whitespace-normal break-words ${!isActive ? 'text-muted-foreground' : ''}`}>
              {getValue() as string}
            </span>
          </div>
        );
      },
    },
    {
      header: () => <span className="bg-transparent px-2 py-1 rounded font-semibold text-center block whitespace-normal break-words">
        <HelpPopoverHover config={helpContent?.['table.header.assignedto']}>Assigned To</HelpPopoverHover>
      </span>,
      accessorKey: 'checkDetail.assignedTo',
      cell: ({ getValue }) => {
        const { userIdToName } = useTenancy();
        const userId = getValue() as string;
        const displayName = userIdToName[userId] || 'Deleted or Unknown User';
        return <span className="text-center block whitespace-normal break-words">{displayName}</span>;
      },
    },
    {
      header: () => <span className="bg-transparent px-2 py-1 rounded font-semibold text-center block whitespace-normal break-words">
        <HelpPopoverHover config={helpContent?.['table.header.framework']}>Framework</HelpPopoverHover>
      </span>,
      accessorKey: 'checkDetail.framework',
      cell: ({ getValue }) => {
        const frameworksRaw = getValue();
        let badges: React.ReactNode[] = [];

        // Helper to normalize and render a single framework
        const renderFramework = (fw: any, idx: number) => {
          // Handle string
          if (typeof fw === 'string') {
            return (
              <Badge key={idx} variant={getDocTypeVariant(fw)}>
                <span className="inline-block align-middle whitespace-normal break-words">{cleanStandardName(fw)}</span>
              </Badge>
            );
          }
          // Handle object with frameName (legacy)
          if (fw && typeof fw === 'object') {
            if ('frameName' in fw && typeof fw.frameName === 'string') {
              const label = fw.frameName;
              return (
                <Badge key={idx} variant={getDocTypeVariant(label)}>
                  <span className="inline-block align-middle whitespace-normal break-words">{cleanStandardName(label)}</span>
                </Badge>
              );
            }
            // Handle object with FRAMENAME as array (new)
            if ('FRAMENAME' in fw && Array.isArray(fw.FRAMENAME)) {
              return fw.FRAMENAME.map((item: any, i: number) => {
                const safeLabel = typeof item === 'string' ? item : String(item);
                return (
                  <Badge key={idx * 1000 + i} variant={getDocTypeVariant(safeLabel)}>
                    <span className="inline-block align-middle whitespace-normal break-words">{cleanStandardName(safeLabel)}</span>
                  </Badge>
                );
              });
            }
            // Handle nested arrays
            if (Array.isArray(fw)) {
              return fw.map((item, i) => renderFramework(item, idx * 100 + i));
            }
            // Unknown object, fallback to JSON string (never just keys)
            const label = JSON.stringify(fw);
            return (
              <Badge key={idx} variant={getDocTypeVariant(label)}>
                <span className="inline-block align-middle whitespace-normal break-words">{label}</span>
              </Badge>
            );
          }
          // Fallback for other types
          const label = String(fw);
          return (
            <Badge key={idx} variant={getDocTypeVariant(label)}>
              <span className="inline-block align-middle whitespace-normal break-words">{cleanStandardName(label)}</span>
            </Badge>
          );
        };

        // Handle all known variations
        if (Array.isArray(frameworksRaw)) {
          badges = frameworksRaw.flatMap((fw, idx) => renderFramework(fw, idx));
        } else if (frameworksRaw && typeof frameworksRaw === 'object') {
          badges = Object.values(frameworksRaw).flatMap((fw, idx) => renderFramework(fw, idx));
        } else if (typeof frameworksRaw === 'string') {
          badges = [renderFramework(frameworksRaw, 0)];
        } else if (frameworksRaw != null) {
          badges = [renderFramework(frameworksRaw, 0)];
        }
        if (badges.length === 0) {
          badges = [<span key="none" className="text-muted-foreground">No Framework</span>];
        }
        return (
          <div className="flex flex-wrap gap-2 justify-center whitespace-normal break-words">
            {badges}
          </div>
        );
      },
      id: 'frameworks',
    },
    {
      header: () => <span className="bg-transparent px-2 py-1 rounded font-semibold text-center w-full block">
        <HelpPopoverHover config={helpContent?.['table.header.nextcheckdate']}>Next Check Date</HelpPopoverHover>
      </span>,
      accessorKey: 'checkFrequency.nextCheck',
      cell: ({ row }) => {

        const nextCheck = row.original.checkFrequency.nextCheck;
        let dateStr;
        switch (typeof nextCheck) {
          case 'object':
            dateStr = nextCheck
            break;
          case 'string':
            dateStr = nextCheck
            break;
          default:
            break;
        }
        return <span className="text-center w-full block">{dateStr ? renderDate(dateStr) : 'Not Set'}</span>;
      },
    },
    {
      header: () => <span className="bg-transparent px-2 py-1 rounded font-semibold text-center w-full block">
        <HelpPopoverHover config={helpContent?.['table.header.actions']}>Actions</HelpPopoverHover>
      </span>,
      id: 'actions',
      cell: ({ row }) => {
        const [isRestoreDialogOpen, setIsRestoreDialogOpen] = useState(false);
        
        const handleRestore = () => {
          onActions.onRestore?.(row.original);
          setIsRestoreDialogOpen(false);
        };
        
        if (showDeletedTasks) {
          // Deleted view - show restore actions
          return (
            <>
              <div className="flex gap-1 justify-center">
                {/* Show History */}
                <Button variant="ghost" size="icon" onClick={() => onActions.onHistory?.(row.original)} title="History">
                  <History className="h-6 w-6" />
                </Button>
                {/* Restore button */}
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => setIsRestoreDialogOpen(true)} 
                  title="Restore Task"
                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                >
                  <RotateCcw className="h-6 w-6" />
                </Button>
                {/* Edit button for deleted tasks */}
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => onActions.onEdit?.(row.original)} 
                  title="Edit Task"
                >
                  <FontAwesomeIcon icon={faPencil} className="h-8 w-8" />
                </Button>
              </div>
              
              {/* Restore confirmation dialog */}
              <AlertDialog open={isRestoreDialogOpen} onOpenChange={setIsRestoreDialogOpen}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Restore Assurance Task</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to restore this assurance task? This action will:
                      <ul className="list-disc list-inside mt-2 space-y-1">
                        <li>Make the task active again in the main list</li>
                        <li>Restore all previous functionality</li>
                        <li>Keep all historical data intact</li>
                      </ul>
                      <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-800">
                        <strong>Task:</strong> {row.original.checkDetail?.check || 'Unknown task'}
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleRestore} className="bg-green-600 text-white hover:bg-green-700">
                      Restore Task
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          );
        } else {
          // Normal view - show regular actions
          return (
            <div className="flex gap-1 justify-center" id={TOUR_STEP_IDS.TASKS_ACTION_BUTTONS}>
              {/* Always show History in both read-only and regular mode */}
              <Button variant="ghost" size="icon" onClick={() => onActions.onHistory?.(row.original)} title="History">
                <History className="h-6 w-6" />
              </Button>
              {/* Only show Edit button in regular mode */}
              {!isReadOnly && (
                <Button variant="ghost" size="icon" onClick={() => onActions.onEdit?.(row.original)} title="Edit">
                  <FontAwesomeIcon icon={faPencil} className="h-8 w-8" />
                </Button>
              )}
              {/* In read-only mode, show Eye icon instead of the clipboard icon */}
              <Button variant="ghost" size="icon" onClick={() => onActions.onPerform?.(row.original)} title="View">
                {isReadOnly ? 
                  <Eye className="h-6 w-6" /> : 
                  <FontAwesomeIcon icon={faClipboardListCheck} className="h-8 w-8" />
                }
              </Button>
            </div>
          );
        }
      },
      enableSorting: false,
    },
  ];
};
